/* eslint-disable react-hooks/exhaustive-deps */
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrintIcon from "@mui/icons-material/LocalPrintshop";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { esArray } from "src/general_components/functionEsArray";
import { createComprobantes } from "src/general_helpers/ComprobantesData";
import { getComprobantesCodigos } from "src/general_helpers/ComprobantesCodigosData";
import { PageHeaderCard } from "src/general_components/PageHeaderCard";
import { SimpleDialogComponent } from "src/general_components/SimpleDialogComponent";
import { SnackbarMessenger } from "src/general_components/SnackbarMessenger";
import { getCuentasJuridicas } from "src/projects/gestion_comercial/helpers/CuentasJuridicasData";
import { getAlicuotasIVA } from "src/projects/articulos/helpers/AlicuotasIVAData";
import { getArticulos } from "src/projects/articulos/helpers/ArticulosData";
import { getTipoMoneda } from "src/projects/articulos/helpers/TipoMonedaData";
import { getOwnerLocalizaciones } from "src/general_components/OwnerLocalizacionesData";
import { getTerminalData } from "src/projects/gestion_documental/helpers/TerminalData";
import { getCodigoCajasData } from "src/projects/gestion_documental/helpers/CogidoCajasData";
import { getTipoMovimientosData } from "src/projects/gestion_documental/helpers/TipoMovimientosData";
import { ModalAgregarItem } from "../components/ModalAgregarItem";

const extractErrorMessage = (message) => {
  if (!message) return "Ocurrió un error al generar el comprobante.";
  if (typeof message === "string") return message;
  // El backend devuelve CHtml::errorSummary como objeto {div: {ul: {li: [...]}}}
  const li = message?.div?.ul?.li;
  if (li) return Array.isArray(li) ? li.join(" | ") : String(li);
  return "Ocurrió un error al generar el comprobante.";
};

let _itemIdCounter = 0;

const formatMoney = (v) =>
  parseFloat(v || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function ComprobantesCargaArticulos() {
  // ── Selectores de cabecera ───────────────────────────────────────────────
  const [cliente, setCliente] = useState(null);
  const [clienteOptions, setClienteOptions] = useState([]);

  const [codigoComprobante, setCodigoComprobante] = useState(null);
  const [codigoComprobanteOptions, setCodigoComprobanteOptions] = useState([]);

  const [fechaComprobante, setFechaComprobante] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [moneda, setMoneda] = useState(null);
  const [monedaOptions, setMonedaOptions] = useState([]);
  const [monedaUnica, setMonedaUnica] = useState(false);

  const [alicuota, setAlicuota] = useState(null);
  const [alicuotaOptions, setAlicuotaOptions] = useState([]);

  const [observaciones, setObservaciones] = useState("");

  // ── Items ────────────────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [articuloOptions, setArticuloOptions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  // ── Campos requeridos por el backend (cargados silenciosamente) ──────────
  const [ownerLocalizacion, setOwnerLocalizacion] = useState(null);
  const [terminal, setTerminal] = useState(null);
  const [codigoCaja, setCodigoCaja] = useState(null);
  const [tipoMovimiento, setTipoMovimiento] = useState(null);

  // ── Proceso ──────────────────────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [idComprobanteGenerado, setIdComprobanteGenerado] = useState(null);

  // ── Feedback ─────────────────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // ── Carga inicial ────────────────────────────────────────────────────────
  useEffect(() => {
    const cargar = async () => {
      // Clientes jurídicos
      const juridicas = esArray(await getCuentasJuridicas()).map((c) => ({
        ...c,
        label: c.id_cuentas + " - " + c.razon_social,
        value: c.id_cuentas,
      }));
      setClienteOptions(juridicas);
      if (juridicas.length === 1) setCliente(juridicas[0]);

      // Códigos de comprobante
      const codigos = esArray(await getComprobantesCodigos()).map((c) => ({
        ...c,
        label: c.id + " - " + c.descripcion,
        value: c.id,
      }));
      setCodigoComprobanteOptions(codigos);
      if (codigos.length === 1) setCodigoComprobante(codigos[0]);

      // Moneda
      const monedas = esArray(await getTipoMoneda()).map((m) => ({
        ...m,
        label: m.id + " - " + m.unidad_monetaria,
        value: m.id,
      }));
      setMonedaOptions(monedas);
      if (monedas.length === 1) {
        setMoneda(monedas[0]);
        setMonedaUnica(true);
      }

      // Alícuotas IVA
      const alicuotas = esArray(await getAlicuotasIVA()).map((a) => ({
        ...a,
        label: a.nombre + (parseFloat(a.valor) > 0 ? ` (${a.valor}%)` : ""),
        value: a.id,
        porcentaje: parseFloat(a.valor) || 0,
      }));
      setAlicuotaOptions(alicuotas);
      if (alicuotas.length === 1) setAlicuota(alicuotas[0]);

      // Artículos (para el modal)
      const respArticulos = await getArticulos();
      setArticuloOptions(esArray(respArticulos?.data));

      // Campos requeridos por el backend — se usan en el payload, no se muestran en el formulario
      const ownerLocs = esArray(await getOwnerLocalizaciones());
      if (ownerLocs.length > 0) setOwnerLocalizacion(ownerLocs[0]);

      const terminales = esArray(await getTerminalData());
      if (terminales.length > 0) setTerminal(terminales[0]);

      const codigosCaja = esArray(await getCodigoCajasData());
      if (codigosCaja.length > 0) setCodigoCaja(codigosCaja[0]);

      const tiposMovimiento = esArray(await getTipoMovimientosData());
      if (tiposMovimiento.length > 0) setTipoMovimiento(tiposMovimiento[0]);
    };
    cargar();
  }, []);

  // ── Cálculos ─────────────────────────────────────────────────────────────
  const neto = items.reduce((acc, item) => acc + item.importe * item.cantidad, 0);
  const iva = alicuota?.porcentaje > 0 ? neto * (alicuota.porcentaje / 100) : 0;
  const total = neto + iva;

  // ── Items handlers ───────────────────────────────────────────────────────
  const handleAgregarItem = (nuevoItem) => {
    setItems((prev) => [...prev, { ...nuevoItem, _id: ++_itemIdCounter }]);
  };

  const handleEliminarItem = (id) => {
    setItems((prev) => prev.filter((i) => i._id !== id));
  };

  // ── Validación ───────────────────────────────────────────────────────────
  const validar = () => {
    if (!cliente) return "Seleccioná un cliente.";
    if (!codigoComprobante) return "Seleccioná un código de comprobante.";
    if (!fechaComprobante) return "Ingresá la fecha del comprobante.";
    if (!moneda) return "Seleccioná la moneda.";
    if (!alicuota) return "Seleccioná una alícuota IVA.";
    if (items.length === 0) return "Agregá al menos un ítem.";
    if (total <= 0) return "El total debe ser mayor a 0.";
    return null;
  };

  const handleProcesar = () => {
    const error = validar();
    if (error) {
      setSnackbar({ open: true, message: error, severity: "warning" });
      return;
    }
    setConfirmOpen(true);
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleConfirmar = async () => {
    setConfirmOpen(false);
    setProcesando(true);

    const comprobantes_valores_items = items.map((item, i) => {
      const subtotal = item.importe * item.cantidad;
      const ivaItem = alicuota.porcentaje > 0 ? subtotal * (alicuota.porcentaje / 100) : 0;
      return {
        id_articulos: item.id_articulos,
        nro_orden: String(i + 1),
        descripcion: item.nombre,
        id_alicuotas_iva: alicuota.value,
        cantidad: item.cantidad,
        precio_unitario: item.importe,
        total_neto_bruto: subtotal,
        porcentaje_descuentos_recargos: 0,
        total_descuentos_recargos: 0,
        total_neto: subtotal,
        total_iva: ivaItem,
        total_impuesto_interno: 0,
        total_otros_impuestos: 0,
        total: subtotal + ivaItem,
      };
    });

    const datos = {
      comprobantes: {
        id_ejercicios_contables: "1",
        id_owner_localizaciones: ownerLocalizacion?.id,
        id_terminal: terminal?.id,
        id_codigo_caja: codigoCaja?.id,
        id_tipo_movimiento: tipoMovimiento?.id,
        fecha_comprobante: fechaComprobante,
        fecha_trabajo: fechaComprobante,
        id_comprobantes_codigos: codigoComprobante.value,
        numero_comprobante: 0,
        afecta_contabilidad: "0",
        total: total,
        observaciones_accesorias: observaciones,
        anulado: "0",
        punto_venta_comprobante: "1",
      },
      comprobantes_valores_items,
      comprobantes_moneda_extranjera: {
        id_tipo_moneda: moneda.value,
        saldo_moneda_extranjera: "0",
        total_moneda_extranjera: total,
        tipo_cambio: 1,
      },
      comprobantes_cuentas: {
        id_cuentas: cliente.value,
      },
    };

    try {
      const response = await createComprobantes(datos);
      if (response?.status === "true") {
        setIdComprobanteGenerado(response?.wsResponse?.id_comprobantes ?? null);
        setPrintDialogOpen(true);
        handleLimpiar();
      } else {
        setSnackbar({
          open: true,
          message: extractErrorMessage(response?.message),
          severity: "error",
        });
      }
    } catch {
      setSnackbar({ open: true, message: "Error inesperado en el servidor.", severity: "error" });
    } finally {
      setProcesando(false);
    }
  };

  // ── Limpiar ──────────────────────────────────────────────────────────────
  const handleLimpiar = () => {
    setCliente(clienteOptions.length === 1 ? clienteOptions[0] : null);
    setCodigoComprobante(codigoComprobanteOptions.length === 1 ? codigoComprobanteOptions[0] : null);
    setFechaComprobante(new Date().toISOString().split("T")[0]);
    if (!monedaUnica) setMoneda(null);
    setAlicuota(alicuotaOptions.length === 1 ? alicuotaOptions[0] : null);
    setObservaciones("");
    setItems([]);
  };

  const handleImprimir = (view) => {
    const credentials = JSON.parse(localStorage.getItem("clientData"));
    if (!credentials || !idComprobanteGenerado) return;
    const { dominio, owner_code } = credentials;
    const cleanDomain = (d) => (typeof d === "string" ? d.replace(/\/+$/, "") : "");
    const viewParam = view === "full" ? "&view=full" : "";
    const url = `${cleanDomain(dominio)}/comprobantes/getComprobanteView/${idComprobanteGenerado}?owner_code=${owner_code}${viewParam}`;
    window.open(url, "_blank");
    setPrintDialogOpen(false);
  };

  return (
    <Grid style={{ marginBottom: "50px", paddingTop: 10 }}>
      <PageHeaderCard title="Generación de comprobantes" icon={<ReceiptLongIcon />} />

      {/* ── Cabecera del comprobante ── */}
      <Card elevation={3} sx={{ mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <DescriptionOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600} color="primary">
              Datos del comprobante
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {/* Cliente */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={clienteOptions}
                getOptionLabel={(o) => o.label ?? ""}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                value={cliente}
                onChange={(_, v) => setCliente(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente *" size="small" />
                )}
              />
            </Grid>

            {/* Código comprobante */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={codigoComprobanteOptions}
                getOptionLabel={(o) => o.label ?? ""}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                value={codigoComprobante}
                onChange={(_, v) => setCodigoComprobante(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Código de comprobante *" size="small" />
                )}
              />
            </Grid>

            {/* Fecha */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fecha del comprobante *"
                type="date"
                size="small"
                fullWidth
                value={fechaComprobante}
                onChange={(e) => setFechaComprobante(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Moneda */}
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={monedaOptions}
                getOptionLabel={(o) => o.label ?? ""}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                value={moneda}
                onChange={(_, v) => setMoneda(v)}
                disabled={monedaUnica}
                renderInput={(params) => (
                  <TextField {...params} label="Moneda *" size="small" />
                )}
              />
            </Grid>

            {/* Alícuota */}
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={alicuotaOptions}
                getOptionLabel={(o) => o.label ?? ""}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                value={alicuota}
                onChange={(_, v) => setAlicuota(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Alícuota IVA *" size="small" />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Ítems ── */}
      <Card elevation={3} sx={{ mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} color="primary">
              Ítems
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setModalOpen(true)}
              disabled={!cliente}
            >
              Agregar ítem
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {!cliente && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Seleccioná un cliente antes de agregar ítems para obtener los precios correctos.
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Artículo</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 130 }} align="right">
                    Importe unit.
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90 }} align="right">
                    Cantidad
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 130 }} align="right">
                    Total
                  </TableCell>
                  <TableCell sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.disabled" }}>
                      Sin ítems — agregá al menos uno para continuar.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, i) => (
                    <TableRow key={item._id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell sx={{ color: "text.secondary" }}>{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.nombre}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">$ {formatMoney(item.importe)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{item.cantidad}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          $ {formatMoney(item.importe * item.cantidad)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Eliminar ítem">
                          <IconButton size="small" color="error" onClick={() => handleEliminarItem(item._id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ── Observaciones e Importes ── */}
      <Card elevation={3} sx={{ mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={3}>
            {/* Observaciones */}
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                Observaciones
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                size="small"
                placeholder="Observaciones del comprobante (opcional)"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
              />
            </Grid>

            {/* Importes calculados */}
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                Importes
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ color: "text.secondary" }}>Neto</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          $ {formatMoney(neto)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {alicuota?.porcentaje > 0 && (
                      <TableRow>
                        <TableCell sx={{ color: "text.secondary" }}>
                          IVA ({alicuota.porcentaje}%)
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            $ {formatMoney(iva)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight={700} color="primary">
                          $ {formatMoney(total)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Acciones ── */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<HighlightOffIcon />}
          onClick={handleLimpiar}
          disabled={procesando}
        >
          Limpiar
        </Button>
        <Tooltip title={procesando ? "Procesando, aguardá..." : ""}>
          <span>
            <Button
              variant="contained"
              color="primary"
              startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : <PlayCircleOutlineIcon />}
              onClick={handleProcesar}
              disabled={procesando}
            >
              {procesando ? "Procesando..." : "Procesar"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* ── Modal agregar ítem ── */}
      <ModalAgregarItem
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleAgregarItem}
        articulos={articuloOptions}
        idCuenta={cliente?.value}
      />

      {/* ── Diálogo de confirmación ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PlayCircleOutlineIcon color="primary" />
          Confirmar generación
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Revisá los datos antes de confirmar. Esta acción generará el comprobante.
          </Alert>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Cliente</Typography>
              <Typography variant="body2" fontWeight={600}>{cliente?.label}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Código</Typography>
              <Typography variant="body2" fontWeight={600}>{codigoComprobante?.label}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Fecha</Typography>
              <Typography variant="body2" fontWeight={600}>{fechaComprobante}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Moneda</Typography>
              <Typography variant="body2" fontWeight={600}>{moneda?.label}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Alícuota IVA</Typography>
              <Typography variant="body2" fontWeight={600}>{alicuota?.label}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Ítems</Typography>
              <Typography variant="body2" fontWeight={600}>{items.length}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">Neto</Typography>
            <Typography variant="body2" fontWeight={600}>$ {formatMoney(neto)}</Typography>
          </Box>
          {alicuota?.porcentaje > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">IVA ({alicuota.porcentaje}%)</Typography>
              <Typography variant="body2" fontWeight={600}>$ {formatMoney(iva)}</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
            <Typography variant="subtitle1" fontWeight={700} color="primary">
              $ {formatMoney(total)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmar}
            startIcon={<CheckCircleIcon />}
          >
            Confirmar y generar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog de impresión post-creación ── */}
      {printDialogOpen && (
        <SimpleDialogComponent
          open={printDialogOpen}
          modalName="Comprobante generado exitosamente"
          handleClose={() => setPrintDialogOpen(false)}
          buttonEnviar={false}
          maxWidth="xs"
          child={
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="body2">
                  El comprobante <strong>#{idComprobanteGenerado}</strong> fue generado exitosamente.
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                ¿Querés imprimirlo?
              </Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PrintIcon />}
                  onClick={() => handleImprimir()}
                >
                  Sin importes
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<PrintIcon />}
                  onClick={() => handleImprimir("full")}
                >
                  Con importes
                </Button>
              </Box>
            </Box>
          }
        />
      )}

      <SnackbarMessenger
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        close={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Grid>
  );
}
